import React, { useState, useEffect } from 'react';
import { Menu, MenuItem, MenuList, Tooltip, Typography } from '@mui/material';
import history from 'common/history';
import { useDispatch } from 'react-redux';
import { setCurrentSelectedFile } from '../redux/setCurrentSelectedFile';
import { setDocSearch } from 'features/viewing/redux/setDocSearch';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { fetchFileDetails } from '../redux/fetchFileDetails';

interface SelectedMatchesContainerProps {
  matchesItm: any;
  handleMatchesMenuClose: any;
  anchorEl: any;
  openAnswerMenu: boolean;
  parentContainerId: any;
  isTranscriptPage?: boolean;
  fileSidebarAskAI?: boolean;
  viewerControl?: any;
}

const SelectedMatchesContainer = ({
  matchesItm,
  handleMatchesMenuClose,
  anchorEl,
  openAnswerMenu,
  parentContainerId,
  isTranscriptPage,
  fileSidebarAskAI,
  viewerControl,
}: SelectedMatchesContainerProps) => {
  const dispatch = useDispatch();
  const [localMenuOpen, setLocalMenuOpen] = useState(openAnswerMenu);

  // Sync local state with parent prop
  useEffect(() => {
    setLocalMenuOpen(openAnswerMenu);
  }, [openAnswerMenu]);

  const handleLocalMenuClose = () => {
    setLocalMenuOpen(false);
    // Only call parent close handler after a short delay to prevent scroll issues
    setTimeout(() => {
      handleMatchesMenuClose(parentContainerId);
    }, 100);
  };

  const highlightText = (text: string, highlight: string) => {
    if (!highlight) return text;
    const regex = new RegExp(highlight, 'gi');
    const matches = text.match(regex);
    if (matches) {
      return text.replace(regex, `<span style="background-color: #ffff00">${matches[0]}</span>`);
    }
    return text;
  };

  // Function to clear existing highlights with subType 'highlightEvent' or 'markButtonTrigger'
  const clearExistingHighlights = (viewerCtrl: any) => {
    try {
      if (typeof viewerCtrl.getAllMarks === 'function') {
        const allMarks = viewerCtrl.getAllMarks();
        if (allMarks && Array.isArray(allMarks)) {
          const highlightMarks = allMarks.filter((mark: any) => {
            try {
              const subType =
                mark && typeof mark.getData === 'function' ? mark.getData('subType') : null;
              return subType === 'highlightEvent' || subType === 'markButtonTrigger';
            } catch {
              return false;
            }
          });

          if (highlightMarks.length > 0 && typeof viewerCtrl.deleteMarks === 'function') {
            // Force hide mark buttons immediately before deleting marks
            highlightMarks.forEach((mark: any) => {
              try {
                // Dispatch a custom event to force hide mark buttons
                if (mark.getData && mark.getData('subType') === 'markButtonTrigger') {
                  const event = new CustomEvent('forceHideMarkButtons', {
                    detail: { markId: mark.uid },
                  });
                  window.dispatchEvent(event);
                }
              } catch {
                console.warn('Error dispatching hide event for mark:', mark.uid);
              }
            });

            viewerCtrl.deleteMarks(highlightMarks);
          }
        }
      }
    } catch (error) {
      console.error('Error clearing existing highlights:', error);
    }
  };

  const handleItemClick = (item: any) => {
    if (!fileSidebarAskAI) {
      handleLocalMenuClose();
      history.push(
        history.location.pathname.replace(/\/files.*/, ``) +
          `/files/${item.fileId || item.file.id}`,
      );
      if (isTranscriptPage) {
        dispatch(fetchFileDetails({ fileId: item.fileId, firstTrialBookOnly: true })).then(
          (res: any) => {
            dispatch(setCurrentSelectedFile(res));
          },
        );
      } else dispatch(setCurrentSelectedFile(item.file));
      dispatch(setDocSearch(item.highlight ? item.highlight : item.text, item.location));
    } else {
      setLocalMenuOpen(false);
      if (viewerControl && item.location && item.location.pageNumber) {
        // Clear existing highlights before adding new ones
        clearExistingHighlights(viewerControl);

        try {
          if (typeof viewerControl.setCurrentPage === 'function') {
            viewerControl.setCurrentPage(item.location.pageNumber);
          } else if (typeof viewerControl.setPageNumber === 'function') {
            viewerControl.setPageNumber(item.location.pageNumber);
          }

          if (typeof viewerControl.scrollToAsync === 'function') {
            try {
              const scrollPromise = viewerControl.scrollToAsync({
                pageNumber: item.location.pageNumber,
                x: item.location.x,
                y: item.location.y,
              });

              if (scrollPromise && typeof scrollPromise.then === 'function') {
                scrollPromise.then(() => {
                  // Request page text to ensure it's loaded before highlighting
                  if (typeof viewerControl.requestPageText === 'function') {
                    viewerControl.requestPageText(item.location.pageNumber).then(
                      () => {
                        if (typeof viewerControl.addMark === 'function') {
                          try {
                            const highlightedArea = viewerControl.addMark(
                              item.location.pageNumber,
                              'HighlightAnnotation',
                            );
                            if (
                              highlightedArea &&
                              typeof highlightedArea.setPosition === 'function'
                            ) {
                              highlightedArea
                                .setPosition({
                                  startIndex: item.location.startIndex,
                                  length: item.location.textLength,
                                })
                                .setFillColor('#AFD0F9')
                                .setData(
                                  'subType',
                                  fileSidebarAskAI ? 'markButtonTrigger' : 'highlightEvent',
                                );
                            }
                          } catch (highlightError) {
                            console.error('Error adding highlight:', highlightError);
                          }
                        }
                      },
                      (error: any) => {
                        console.error('Failed to request page text:', error);
                        if (typeof viewerControl.addMark === 'function') {
                          try {
                            const highlightedArea = viewerControl.addMark(
                              item.location.pageNumber,
                              'HighlightAnnotation',
                            );
                            if (
                              highlightedArea &&
                              typeof highlightedArea.setPosition === 'function'
                            ) {
                              highlightedArea
                                .setPosition({
                                  startIndex: item.location.startIndex,
                                  length: item.location.textLength,
                                })
                                .setFillColor('#AFD0F9')
                                .setData(
                                  'subType',
                                  fileSidebarAskAI ? 'markButtonTrigger' : 'highlightEvent',
                                );
                            }
                          } catch (highlightError) {
                            console.error('Error adding highlight:', highlightError);
                          }
                        }
                      },
                    );
                  } else {
                    if (typeof viewerControl.addMark === 'function') {
                      try {
                        const highlightedArea = viewerControl.addMark(
                          item.location.pageNumber,
                          'HighlightAnnotation',
                        );
                        if (highlightedArea && typeof highlightedArea.setPosition === 'function') {
                          highlightedArea
                            .setPosition({
                              startIndex: item.location.startIndex,
                              length: item.location.textLength,
                            })
                            .setFillColor('#AFD0F9')
                            .setData(
                              'subType',
                              fileSidebarAskAI ? 'markButtonTrigger' : 'highlightEvent',
                            );
                        }
                      } catch (highlightError) {
                        console.error('Error adding highlight:', highlightError);
                      }
                    }
                  }
                });
                if (typeof scrollPromise.catch === 'function') {
                  scrollPromise.catch((err: any) => {
                    console.error('Failed to scroll to result:', err);
                  });
                }
              } else {
                if (typeof viewerControl.addMark === 'function') {
                  try {
                    setTimeout(() => {
                      // Request page text first even for non-promise case
                      if (typeof viewerControl.requestPageText === 'function') {
                        viewerControl
                          .requestPageText(item.location.pageNumber)
                          .then(() => {
                            const highlightedArea = viewerControl.addMark(
                              item.location.pageNumber,
                              'HighlightAnnotation',
                            );
                            if (
                              highlightedArea &&
                              typeof highlightedArea.setPosition === 'function'
                            ) {
                              highlightedArea
                                .setPosition({
                                  startIndex: item.location.startIndex,
                                  length: item.location.textLength,
                                })
                                .setFillColor('#AFD0F9')
                                .setData(
                                  'subType',
                                  fileSidebarAskAI ? 'markButtonTrigger' : 'highlightEvent',
                                );
                            }
                          })
                          .catch((error: any) => {
                            console.error('Failed to request page text (non-promise):', error);
                            // Final fallback without page text
                            const highlightedArea = viewerControl.addMark(
                              item.location.pageNumber,
                              'HighlightAnnotation',
                            );
                            if (
                              highlightedArea &&
                              typeof highlightedArea.setPosition === 'function'
                            ) {
                              highlightedArea
                                .setPosition({
                                  startIndex: item.location.startIndex,
                                  length: item.location.textLength,
                                })
                                .setFillColor('#AFD0F9')
                                .setData(
                                  'subType',
                                  fileSidebarAskAI ? 'markButtonTrigger' : 'highlightEvent',
                                );
                            }
                          });
                      } else {
                        const highlightedArea = viewerControl.addMark(
                          item.location.pageNumber,
                          'HighlightAnnotation',
                        );
                        if (highlightedArea && typeof highlightedArea.setPosition === 'function') {
                          highlightedArea
                            .setPosition({
                              startIndex: item.location.startIndex,
                              length: item.location.textLength,
                            })
                            .setFillColor('#AFD0F9')
                            .setData(
                              'subType',
                              fileSidebarAskAI ? 'markButtonTrigger' : 'highlightEvent',
                            );
                        }
                      }
                    }, 100);
                  } catch (highlightError) {
                    console.error('Error adding highlight:', highlightError);
                  }
                }
              }
            } catch (scrollError) {
              console.error('Error during scroll operation:', scrollError);
            }
          }
        } catch (err) {
          console.error('Error navigating to search result:', err);
        }
      }

      // Call parent close handler after highlighting is complete
      setTimeout(() => {
        handleMatchesMenuClose(parentContainerId);
      }, 300);
    }
  };
  return (
    <Menu
      id="answer-macthes-menu"
      anchorEl={anchorEl}
      open={localMenuOpen}
      onClose={handleLocalMenuClose}
      MenuListProps={{
        'aria-labelledby': 'basic-button',
      }}
      slotProps={{
        paper: {
          sx: {
            boxShadow: '0 1px 3px rgba(205, 205, 205, 0.12), 0 1px 2px rgba(205, 205, 205, 0.24)',
          },
        },
      }}
    >
      <MenuList>
        <PerfectScrollbar options={{ suppressScrollX: true }} style={{ maxHeight: '30vh' }}>
          {matchesItm.map((item: any, index: number) => (
            <Tooltip
              key={index}
              title={
                <div
                  dangerouslySetInnerHTML={{ __html: highlightText(item.text, item.highlight) }}
                ></div>
              }
              placement="right"
              arrow
            >
              <MenuItem key={index} onClick={() => handleItemClick(item)}>
                <Typography sx={{ fontWeight: 600, color: '#0000007a', mr: '1rem' }}>
                  {item.srcIndex}
                </Typography>
                <Typography sx={{ fontWeight: 600, color: '#0000007a', mr: '1rem' }}>
                  {item.fileId || item.file.id}
                </Typography>
                <Typography sx={{ fontWeight: 600, color: '#0000007a' }}>
                  {item.fileName || item.filename || item.file.name}
                </Typography>
              </MenuItem>
            </Tooltip>
          ))}
        </PerfectScrollbar>
      </MenuList>
    </Menu>
  );
};

export default SelectedMatchesContainer;
